import type { Budget, BudgetStatus, CostsState } from '@orgus/protocol';
import { ChartNoAxesColumn, ChartSpline } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { BurnChart, ColumnChart, LiveNumber, Panel, RingMeter, RowStatus, Segmented, cn, formatCompact, formatDate, formatMoney, formatMoneyCompact, formatPercent, plural } from '@orgus/ui';
import type { RowStatusProps } from '@orgus/ui';
import { cacheHitRatio, dayLabel, forecastState, monthBurn, spendSummary } from './model.ts';

/** One figure in the hero's figure row: label, a large rolling value, one line under it. */
/** `featured`: the row's one brand figure, in the Prism headline gradient (colour.md, the warm role). */
function Figure({ label, value, format, sub, aside, featured }: { label: string; value: number; format: (n: number) => string; sub: ReactNode; aside?: ReactNode; featured?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-1" data-metric={label}>
      <span className="truncate text-caption text-fg-subtle">{label}</span>
      <span className="flex h-8 min-w-0 items-center gap-3">
        <LiveNumber value={value} format={format} className={cn('font-display text-metric num-display', featured && value > 0 ? 'prism-headline' : 'text-fg')} />
        {aside}
      </span>
      <span className="min-w-0 truncate text-caption text-fg-subtle">{sub}</span>
    </div>
  );
}

/** The month's forecast against its budget, as one status: grey on track, amber heading over, red over. */
function forecastLine(s: BudgetStatus): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  const state = forecastState(s);
  const gap = Math.abs(s.limit - s.forecast);
  // One short fact each, so the line fits a phone's half-width figure.
  if (state === 'over') return { tone: 'danger', icon: 'failed', label: `Over by ${formatMoney(s.spent - s.limit)}` };
  if (state === 'near') return { tone: 'attention', icon: 'warning', label: `${formatMoney(gap)} ${s.forecast > s.limit ? 'over' : 'under'}` };
  if (state === 'forecast') return { tone: 'attention', icon: 'warning', label: `${formatMoney(gap)} over` };
  return { tone: 'neutral', icon: 'done', label: `${formatMoney(gap)} under` };
}

/**
 * Will we stay in budget: the month's figures in one row, then the running total against the
 * budget with its forecast (or the daily spend as columns), in one panel.
 */
export function SpendHero({
  costs,
  orgMonth,
  orgStatus,
  dailyLimit,
  actions,
  partial,
  className,
}: {
  costs: CostsState;
  orgMonth?: Budget;
  orgStatus?: BudgetStatus;
  dailyLimit?: number | null;
  actions?: ReactNode;
  partial?: ReactNode;
  className?: string;
}) {
  const [view, setView] = useState<'running' | 'daily'>('running');
  const burn = useMemo(() => monthBurn(costs), [costs]);
  const hit = cacheHitRatio(costs.tokensMonth);
  const sum = spendSummary(costs);
  const lastDay = formatDate(costs.monthEnd - 1, costs.monthEnd - 1);
  const used = orgStatus && orgStatus.limit > 0 ? orgStatus.spent / orgStatus.limit : null;
  const firstOfMonth = costs.byDay.length - burn.actual.length;
  const daily = costs.byDay.map((b) => b.cost);
  const xLabels = [0, Math.floor(daily.length / 2), daily.length - 1].map((i) => ({ index: i, label: i === daily.length - 1 ? 'Today' : dayLabel(costs.byDay[i]!.key) }));

  return (
    <Panel
      title="Spend against budget"
      sub={orgMonth ? undefined : 'No monthly budget yet'}
      className={className}
      actions={
        <span className="flex items-center gap-3">
          {actions}
          <Segmented
            label="Chart"
            value={view}
            onChange={setView}
            size="sm"
            options={[
              { value: 'running', label: 'Running total', icon: <ChartSpline />, iconOnly: true },
              { value: 'daily', label: 'Per day', icon: <ChartNoAxesColumn />, iconOnly: true },
            ]}
          />
        </span>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-2 gap-x-8 gap-y-4 *:min-w-0 lg:grid-cols-4">
          <Figure
            label="Month to date"
            value={sum.month}
            format={formatMoney}
            sub={orgStatus ? `${formatPercent(used ?? 0)} of ${formatMoney(orgStatus.limit)}` : `${plural(sum.monthSessions, 'session')} this month`}
            aside={used != null ? <RingMeter value={used} size={32} tone={1} label={`${formatPercent(used)} of the monthly budget used`} /> : undefined}
          />
          <Figure
            label={`Forecast, ${lastDay}`}
            value={costs.forecastMonth}
            format={formatMoney}
            sub={orgStatus ? <RowStatus {...forecastLine(orgStatus)} className="min-w-0 max-w-full text-caption" /> : 'At this month’s pace'}
          />
          <Figure label="Today" value={sum.today} format={formatMoney} sub={sum.todaySessions ? plural(sum.todaySessions, 'session') : 'Nothing yet today'} />
          <Figure label="Cache hit" featured value={hit ?? 0} format={(n) => (hit == null ? 'None' : formatPercent(n))} sub={hit == null ? 'No input tokens yet' : `${formatCompact(costs.tokensMonth.cacheRead)} from cache`} />
        </div>

        {view === 'running' ? (
          <BurnChart
            actual={burn.actual}
            days={burn.days}
            forecast={costs.forecastMonth}
            budget={orgStatus?.limit ?? null}
            format={formatMoney}
            axisFormat={formatMoneyCompact}
            dayLabel={burn.dayLabel}
            height={232}
            label={`${formatMoney(costs.month)} spent so far this month${orgStatus ? ` of a ${formatMoney(orgStatus.limit)} budget` : ''}; forecast ${formatMoney(costs.forecastMonth)} by ${lastDay}`}
          />
        ) : (
          <ColumnChart
            values={daily}
            contextBefore={Math.max(0, firstOfMonth)}
            columnLabel={(i) => dayLabel(costs.byDay[i]!.key)}
            columnNote={(i) => <span className="text-fg-muted">{plural(costs.byDay[i]!.sessions, 'session')}</span>}
            format={formatMoney}
            axisFormat={formatMoneyCompact}
            xLabels={xLabels}
            threshold={dailyLimit ? { value: dailyLimit, label: `Daily budget ${formatMoneyCompact(dailyLimit)}` } : undefined}
            size="lg"
            label={`Spend per day for the last ${daily.length} days, ${formatMoney(daily.reduce((a, n) => a + n, 0))} in all`}
          />
        )}
        {partial && <p className="text-caption text-fg-subtle">{partial}</p>}
      </div>
    </Panel>
  );
}
